import { Avatar, styled } from '@mui/material'
import { useState, useCallback } from 'react'
import { formatDateTimeMinute } from '../../../../../utils/date'
import { RichTextEditor } from './RichTextEditor'
import { RichTextContent } from './RichTextContent'
import { DiscussionThread as DiscussionThreadType } from '../../../../../domain/entity/DiscussionEntity'
import { intl } from '../../../../../i18n'
import { useDiscussionActions } from './hooks/useDiscussionActions'
import { colorPalette } from '../../../../style/colorPalette'

type DiscussionThreadProps = {
  thread: DiscussionThreadType
  projectUuid: string
  versionPageUuid: string
  onRefresh: () => void
  readOnly?: boolean
}

export const DiscussionThread = ({
  thread,
  projectUuid,
  versionPageUuid,
  onRefresh,
  readOnly = false,
}: DiscussionThreadProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)

  const { handleCreateReply } = useDiscussionActions({
    versionPageUuid,
    onRefresh,
  })

  const handleReplySubmit = useCallback(
    async (content: string) => {
      if (content.trim()) {
        await handleCreateReply(thread.threadUuid, content.trim())
        setShowReplyForm(false)
      }
    },
    [handleCreateReply, thread.threadUuid]
  )

  return (
    <ThreadItem>
      <ThreadComments
        thread={thread}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        showReplyForm={showReplyForm}
        onSetShowReplyForm={setShowReplyForm}
        onReplySubmit={handleReplySubmit}
        projectUuid={projectUuid}
        readOnly={readOnly}
      />
    </ThreadItem>
  )
}

type ThreadCommentsProps = {
  thread: DiscussionThreadType
  isExpanded: boolean
  onToggleExpanded: () => void
  showReplyForm: boolean
  onSetShowReplyForm: (show: boolean) => void
  onReplySubmit: (content: string) => void
  projectUuid?: string
  readOnly: boolean
}

const ThreadComments = ({
  thread,
  isExpanded,
  onToggleExpanded,
  showReplyForm,
  onSetShowReplyForm,
  onReplySubmit,
  projectUuid,
  readOnly,
}: ThreadCommentsProps) => {
  const firstComment = thread.discussionComments[0]
  const remainingComments = thread.discussionComments.slice(1)
  const visibleTargets = thread.targets.filter(t => t.targetName !== null)

  return (
    <>
      {visibleTargets.length > 0 && (
        <ThreadHeading>
          {visibleTargets.map(target => (
            <TargetNameLine key={target.targetUuid}>
              {target.targetName}
            </TargetNameLine>
          ))}
        </ThreadHeading>
      )}
      <CommentItem>
        <AuthorRow>
          <CommenterAvatar
            src={firstComment.commenterIconUrl}
            alt={firstComment.commentedBy}
          />
          <CommentAuthor>{firstComment.commentedBy}</CommentAuthor>
          <CommentTime>
            {formatDateTimeMinute(firstComment.createdAt)}
            {firstComment.isEdited && (
              <EditedLabel>
                {intl.formatMessage({ id: 'discussion.edited' })}
              </EditedLabel>
            )}
          </CommentTime>
        </AuthorRow>
        <RichTextContent content={firstComment.content} />
      </CommentItem>

      {remainingComments.length > 0 && (
        <RepliesToggle onClick={onToggleExpanded}>
          {isExpanded
            ? intl.formatMessage({ id: 'discussion.reply.hide' })
            : intl.formatMessage(
                { id: 'discussion.reply.show' },
                { n: remainingComments.length }
              )}
        </RepliesToggle>
      )}

      {isExpanded && (
        <>
          {remainingComments.map(comment => (
            <ReplyItem key={comment.commentUuid}>
              <AuthorRow>
                <CommenterAvatar
                  src={comment.commenterIconUrl}
                  alt={comment.commentedBy}
                />
                <CommentAuthor>{comment.commentedBy}</CommentAuthor>
                <CommentTime>
                  {formatDateTimeMinute(comment.createdAt)}
                  {comment.isEdited && (
                    <EditedLabel>
                      {intl.formatMessage({ id: 'discussion.edited' })}
                    </EditedLabel>
                  )}
                </CommentTime>
              </AuthorRow>
              <RichTextContent content={comment.content} />
            </ReplyItem>
          ))}
          {!readOnly && (
            <ReplyFormArea onSubmit={onReplySubmit} projectUuid={projectUuid} />
          )}
        </>
      )}

      {!readOnly &&
        remainingComments.length === 0 &&
        (showReplyForm ? (
          <ReplyFormArea
            onSubmit={onReplySubmit}
            onCancel={() => onSetShowReplyForm(false)}
            showCancel={true}
            projectUuid={projectUuid}
          />
        ) : (
          <ReplyToggle onClick={() => onSetShowReplyForm(true)}>
            {intl.formatMessage({ id: 'discussion.reply' })}
          </ReplyToggle>
        ))}
    </>
  )
}

interface ReplyFormAreaProps {
  onSubmit: (content: string) => void
  onCancel?: () => void
  showCancel?: boolean
  projectUuid?: string
}

const ReplyFormArea = ({
  onSubmit,
  onCancel,
  showCancel,
  projectUuid,
}: ReplyFormAreaProps) => {
  return (
    <RichTextEditor
      placeholder={intl.formatMessage({ id: 'discussion.reply.placeholder' })}
      onSubmit={onSubmit}
      onCancel={onCancel}
      showCancel={showCancel}
      submitLabel={intl.formatMessage({ id: 'discussion.reply' })}
      projectUuid={projectUuid}
    />
  )
}

const ThreadItem = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '8px',
  padding: '14px',
  backgroundColor: colorPalette.monotone[1],
})

const ThreadHeading = styled('div')({
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  paddingBottom: '6px',
  marginBottom: '8px',
})

const TargetNameLine = styled('div')({
  fontSize: '14px',
  fontWeight: 600,
  color: colorPalette.monotone[10],
  lineHeight: 1.5,
  wordBreak: 'break-word',
})

const CommentItem = styled('div')({
  marginBottom: '8px',
  '& > div p': {
    margin: '0 0 8px 0',
  },
  '& > div p:first-of-type, & > div > :first-child': {
    marginTop: 0,
  },
  '& > div p:last-of-type': {
    marginBottom: 0,
  },
})

const ReplyItem = styled('div')({
  marginLeft: '16px',
  marginBottom: '14px',
  paddingLeft: '12px',
  borderLeft: `2px solid ${colorPalette.monotone[2]}`,
  '& > div p': {
    margin: '0 0 8px 0',
  },
  '& > div p:first-of-type, & > div > :first-child': {
    marginTop: 0,
  },
  '& > div p:last-of-type': {
    marginBottom: 0,
  },
})

const AuthorRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderLeft: `3px solid ${colorPalette.skyBlue[7]}`,
  marginBottom: '6px',
})

const CommenterAvatar = styled(Avatar)({
  width: 16,
  height: 16,
  flexShrink: 0,
  alignSelf: 'center',
  marginRight: '-4px',
  transform: 'translateY(2px)',
})

const CommentAuthor = styled('span')({
  fontSize: '12px',
  fontWeight: 600,
  color: colorPalette.monotone[10],
})

const CommentTime = styled('span')({
  fontSize: '11px',
  color: colorPalette.monotone[5],
})

const EditedLabel = styled('span')({
  fontSize: '11px',
  color: colorPalette.monotone[5],
  fontStyle: 'italic',
  marginLeft: '8px',
})

const RepliesToggle = styled('button')({
  background: 'none',
  border: 'none',
  color: colorPalette.skyBlue[7],
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 0',
  marginBottom: '8px',
  '&:hover': {
    textDecoration: 'underline',
  },
})

const ReplyToggle = styled('button')({
  background: 'none',
  border: 'none',
  color: colorPalette.skyBlue[7],
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 0',
  '&:hover': {
    textDecoration: 'underline',
  },
})
